import Box from '@mui/material/Box';
import { DependenciesSection } from './DependenciesSection';
import { DetailsSection } from './DetailsSection';
import { useCallback, useMemo, memo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { SearchProvider } from './search/SearchProvider';
import { SWEEP_ELEMENTS, excludedFromDetails, isFieldType } from './helpers';
import { ConfigurationItemMap } from '../configuration-item-tables/shared/helpers';
import { isWhatDoesItDoAllowed } from './configuration-chat/configurationChatConsts';
import OpenAiSection from './open-ai/OpenAiSection';
import { useDependencies } from './hooks/useDependencies';
import { selectOpenAiDescriptionForRule } from '../../../reducers/documentation/documentationReducer';
import { ConfigurationToName } from './types';
import { LatestDeployedSection } from './latest-deployed/LatestDeployedSection';
import { SweepElementTypes } from '../types';
import { useGetUsedBySweepElements } from './hooks/useGetUsedBySweepElements';
import { useGetParsedDependencies } from './hooks/useGetParsedDependencies';
import { useGetSweepElementsDependencies } from './hooks/useGetSweepElementsDependencies';
import { telemetry } from '../../../telemetry';
import { TagsSection } from './annotations/tags/TagsSection';
import { ServiceNowMigrationSection } from '../../pages/org-migration-demo/components/ServiceNowMigrationSection';
import { UserStorySection } from '../../pages/org-migration-demo/components/UserStorySection';
import { UserStory } from '../../pages/org-migration-demo/types';
import { useActiveInactive } from '../activity-state/useActiveInactive';
import { ActiveState } from '../activity-state/helpers';
import { useInitializeOpenAiDescriptionForRule } from './hooks/useInitializeOpenAiDescriptionForRule';
import { DependenciesHeader } from './header/DependenciesHeader';
import { getItemType } from './header/utils';
import { ChatLayer } from './ChatLayer';
import { selectUserInfoData } from '../../../reducers/userInfoReducer';
import { SalesforceItem, isFieldConfigurationItem } from '../../parser/ConfigurationItemTypes';
import { setSfFieldName } from '../../pages/rollups/rollupHelpers';
import { selectSaltoObjectTypes } from '../../../reducers/global/parserReducer';
import { useGetObjectDependencies } from './hooks/useGetObjectDependencies';
import { useLocation } from 'react-router';
import { appRoutes } from '../../../constants/appRoutes';
import { FIELD, OBJECT } from '@server/parser';

interface DependenciesContentProps {
  configurationItem: SalesforceItem;
  type: string;
  crmOrgId: string;
  isReadOnly?: boolean;
}

type OrgMigrationConfigurationItem = SalesforceItem & {
  serviceNowMapping?: string[];
  migrationConsiderations?: string[];
  userStory?: UserStory;
};

const DependenciesContentComponent = ({
  configurationItem,
  type,
  crmOrgId,
  isReadOnly = false,
}: DependenciesContentProps) => {
  const [displayChat, setDisplayChat] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isOrgMigrationRoute = location.pathname.startsWith(appRoutes.orgMigration.route);
  const orgMigrationItem = configurationItem as OrgMigrationConfigurationItem;
  const { id, name, objectApiNames, _oldId } = configurationItem ?? {};
  const dependencyType = 'fieldType' in configurationItem ? configurationItem.fieldType : '';
  const _objectName = objectApiNames?.[0];

  const openAiDescription = useSelector(selectOpenAiDescriptionForRule(id, crmOrgId));
  const isSweepElement = SWEEP_ELEMENTS.includes(type);

  const user = useSelector(selectUserInfoData);
  const disableAi = !!user?.account?.disableAi || isSweepElement;

  const {
    handleSetConfigurationItem,
    getConfigurationDependencies,
    getConfigurationUsedBy,
    getFieldConsumers,
    historyLength,
  } = useDependencies(crmOrgId);

  useInitializeOpenAiDescriptionForRule({ configurationItem, crmOrgId, type });

  //filters below can coexist but they shouldn't share state so 3 separate initializations are necessary
  //dependsOnActiveState, usedByActiveState, recordTypesActiveState,
  const {
    activeState: dependsOnActiveState,
    onToggleActiveInactive: _dependsOnToggleActiveInactive,
  } = useActiveInactive();

  const { activeState: usedByActiveState, onToggleActiveInactive: _usedByOnToggleActiveInactive } =
    useActiveInactive();

  const {
    activeState: configurationsActiveState,
    onToggleActiveInactive: _configurationsOnToggleActiveInactive,
  } = useActiveInactive();

  const _getConfigurationUsedBy = useCallback(async () => {
    if (type && !isSweepElement) {
      switch (type) {
        case FIELD:
          if (name && _objectName && id) {
            await getFieldConsumers({
              metadataId: configurationItem.metadataId,
              fieldName: name,
              objectName: _objectName,
              crmOrgId,
            });
          } else {
            telemetry.captureError(new Error('missing values for getFieldConsumers'), {
              name,
              objectName: _objectName,
            });
          }
          break;

        default:
          await getConfigurationUsedBy({
            ruleMetadataId: configurationItem.metadataId,
            crmOrgId,
            metadataId: configurationItem.metadataId,
          });
      }
    }
  }, [
    type,
    name,
    _objectName,
    getConfigurationUsedBy,
    getFieldConsumers,
    id,
    crmOrgId,
    isSweepElement,
    configurationItem.metadataId,
  ]);

  // custom fields ids are being provided from BE as sf field name + objectName, not as real id
  const configurationIdForSweepElements = isFieldConfigurationItem(configurationItem)
    ? setSfFieldName(configurationItem.name, _objectName)
    : id;

  const dependsOnForSweepElement = useGetSweepElementsDependencies(
    crmOrgId,
    configurationIdForSweepElements,
    type as SweepElementTypes,
    dependsOnActiveState === ActiveState.activeOnly,
  );

  const dependsOnItems = useGetParsedDependencies({
    crmOrgId,
    metadataId: configurationItem.metadataId,
    type: 'dependsOnIdsForRules',
    onlyActive: dependsOnActiveState === ActiveState.activeOnly,
  });

  const usedByItems = useGetParsedDependencies({
    crmOrgId,
    metadataId: configurationItem.metadataId,
    type: isFieldConfigurationItem(configurationItem) ? 'usedByIdsForFields' : 'usedByIdsForRules',
    onlyActive: usedByActiveState === ActiveState.activeOnly,
  });

  const { attributions: usedByObject, sweepElementsAttributions: usedObjectBySweepElements } =
    useGetObjectDependencies({
      crmOrgId,
      objectName: configurationItem.name ?? '',
      isSupportedType: type === OBJECT,
      onlyActive: configurationsActiveState === ActiveState.activeOnly,
    });

  const usedBySweepElements = useGetUsedBySweepElements({
    crmOrgId,
    configurationId: configurationIdForSweepElements,
    onlyActive: usedByActiveState === ActiveState.activeOnly,
  });

  const configurationChatObjectName = _objectName;
  const saltoObjectTypes = useSelector(selectSaltoObjectTypes(crmOrgId));
  const showAiSection = !disableAi && type && isWhatDoesItDoAllowed(type);
  const isField = isFieldType(type);

  const itemType = useMemo(() => {
    return getItemType({
      saltoObjectTypes: saltoObjectTypes ?? [],
      _objectName,
      parentType: type,
      dependencyType,
    });
  }, [saltoObjectTypes, _objectName, type, dependencyType]);

  const configurationItems = useMemo(() => {
    return usedByObject;
  }, [usedByObject]);

  const sweepConfigurationItems = useMemo(() => {
    return usedObjectBySweepElements;
  }, [usedObjectBySweepElements]);

  const relatedListsItems = useMemo(() => {
    const relatedList = 'relatedLists' in configurationItem ? configurationItem.relatedLists : [];
    if (!relatedList?.length) {
      return undefined;
    }
    return {
      relatedList,
    } as unknown as ConfigurationItemMap; //TODO
  }, [configurationItem]);

  const configurationItemsForConfigurations = useMemo(
    () => configurationItems,
    [configurationItems],
  );

  const sweepConfigurationItemsForConfigurations = useMemo(
    () => sweepConfigurationItems,
    [sweepConfigurationItems],
  );

  const usedByItemsForConfigurations = useMemo(() => usedByItems, [usedByItems]);

  const usedBySweepElementsForConfigurations = useMemo(
    () => usedBySweepElements,
    [usedBySweepElements],
  );

  const dependsOnItemsForConfigurations = useMemo(
    () => (isSweepElement ? dependsOnForSweepElement : dependsOnItems),
    [isSweepElement, dependsOnForSweepElement, dependsOnItems],
  );

  const isObjectType = OBJECT === type;

  return (
    <SearchProvider
      configurationItem={configurationItem}
      dependsOnItems={isSweepElement ? dependsOnForSweepElement : dependsOnItems}
      usedByItems={usedByItems}
      usedBySweepItems={usedBySweepElements}
      configurationItems={configurationItems}
      sweepConfigurationItems={sweepConfigurationItems}
      relatedListsItems={relatedListsItems}
      saltoObjectTypes={saltoObjectTypes}
      dependsOnActiveState={dependsOnActiveState}
      usedByActiveState={usedByActiveState}
      configurationsActiveState={configurationsActiveState}
      scrollContainerRef={scrollContainerRef}
    >
      <DependenciesHeader
        configurationItem={configurationItem}
        type={type}
        crmOrgId={crmOrgId}
        historyLength={historyLength}
        isReadOnly={isReadOnly}
        isOrgMigrationRoute={isOrgMigrationRoute}
        showAskAiButton={!!type && !!name && !disableAi && !!configurationItem.metadataId}
        onAskAiClick={() => setDisplayChat(true)}
      />

      <Box position="relative" height="100%" overflow="hidden" width="100%">
        <Box ref={scrollContainerRef} p={2} overflow="scroll" height="100%">
          {showAiSection && (
            <Box pb={2}>
              <OpenAiSection openAiDescription={openAiDescription} />
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, width: '100%' }}>
            {type && !excludedFromDetails.includes(type) && (
              <DetailsSection
                key={`details-${id}`}
                configurationItem={configurationItem}
                isField={isField}
                isExpandedByDefault={!showAiSection}
              />
            )}

            {isObjectType && (
              <DependenciesSection
                metadataId={configurationItem.metadataId}
                crmOrgId={crmOrgId}
                key={`configurations-${id}.${name}`}
                id={id}
                name={name}
                objectName={_objectName}
                configurationItems={configurationItemsForConfigurations}
                sweepConfigurationItems={sweepConfigurationItemsForConfigurations}
                type={type}
                title="Configurations"
                onChooseNewRule={handleSetConfigurationItem}
                onToggleActiveInactive={_configurationsOnToggleActiveInactive}
                activeState={configurationsActiveState}
              />
            )}

            {!isObjectType && (
              <DependenciesSection
                key={`dependsOn-${id}.${name}`}
                metadataId={configurationItem.metadataId}
                id={id}
                name={name}
                objectName={_objectName}
                type={type}
                configurationItems={dependsOnItemsForConfigurations}
                title="Dependencies"
                fetchDependenciesApi={isSweepElement ? undefined : getConfigurationDependencies}
                onChooseNewRule={handleSetConfigurationItem}
                crmOrgId={crmOrgId}
                onToggleActiveInactive={_dependsOnToggleActiveInactive}
                activeState={dependsOnActiveState}
              />
            )}

            {!isObjectType && (
              <DependenciesSection
                metadataId={configurationItem.metadataId}
                crmOrgId={crmOrgId}
                key={`usedBy-${id}.${name}`}
                id={id}
                name={name}
                objectName={_objectName}
                configurationItems={usedByItemsForConfigurations}
                sweepConfigurationItems={usedBySweepElementsForConfigurations}
                type={type}
                title="Used by"
                fetchDependenciesApi={_getConfigurationUsedBy}
                onChooseNewRule={handleSetConfigurationItem}
                onToggleActiveInactive={_usedByOnToggleActiveInactive}
                activeState={usedByActiveState}
              />
            )}

            {isOrgMigrationRoute && orgMigrationItem.serviceNowMapping && (
              <ServiceNowMigrationSection
                title="ServiceNow Mapping"
                items={orgMigrationItem.serviceNowMapping}
                isExpandedByDefault
              />
            )}

            {isOrgMigrationRoute && orgMigrationItem.migrationConsiderations && (
              <ServiceNowMigrationSection
                title="Migration Considerations"
                items={orgMigrationItem.migrationConsiderations}
              />
            )}

            {isOrgMigrationRoute && orgMigrationItem.userStory && (
              <UserStorySection userStory={orgMigrationItem.userStory} />
            )}

            {isSweepElement && (
              <LatestDeployedSection
                sweepElementType={type as SweepElementTypes}
                parentElementId={configurationItem.id}
                crmOrgId={crmOrgId}
              />
            )}

            {/* TODO: temporary fix for missing internal id until BE is migrated to metadataId */}
            {!isReadOnly &&
              !isOrgMigrationRoute &&
              _oldId !== 'MissingInternalId' &&
              type !== OBJECT && (
                <TagsSection
                  crmOrgId={crmOrgId}
                  configurationItemId={configurationItem.id}
                  objectNames={_objectName ? [_objectName] : objectApiNames}
                  configurationItemType={
                    ConfigurationToName[configurationItem.parentType] ??
                    configurationItem.parentType
                  }
                />
              )}
          </Box>
        </Box>
      </Box>

      {displayChat && type && name && (
        <Box
          sx={{
            position: 'absolute',
            top: '28px',
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'background.paper',
            zIndex: 1,
          }}
        >
          <ChatLayer
            type={type}
            name={configurationItem.label}
            configurationItem={configurationItem}
            crmOrgId={crmOrgId}
            itemType={itemType}
            configurationChatObjectName={configurationChatObjectName ?? ''}
            onClose={() => setDisplayChat(false)}
          />
        </Box>
      )}
    </SearchProvider>
  );
};

export const DependenciesContent = memo(DependenciesContentComponent);
